// src/pages/LocationPage.jsx
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocationContext } from '../contexts/LocationContext';
import logoImage from '../assets/logo.png';
import '../styles/location.css';
import SEOHead from '../components/SEO/SEOHead';

const LocationPage = () => {
  const navigate = useNavigate();
  const { userLocation, locationSkipped, enableGeolocation, skipGeolocation, loading, error } = useContext(LocationContext);
  const [locationStatus, setLocationStatus] = useState('');
  const [isIOS, setIsIOS] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [localError, setLocalError] = useState(null); // État local pour gérer les erreurs

  // Vérifier si l'appareil est iOS
  useEffect(() => {
    const checkIsIOS = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      return /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    };
    
    setIsIOS(checkIsIOS());
  }, []);

  // Rediriger si l'utilisateur a déjà une position ou a choisi de passer
  useEffect(() => {
    if (userLocation || locationSkipped) {
      navigate('/prototype-categories');
    }
  }, [userLocation, locationSkipped, navigate]);

  const handleEnableLocation = async () => {
    setLocationStatus('Recherche de votre position...');
    setIsRetrying(false);
    setLocalError(null); // Réinitialiser l'erreur locale
    
    try {
      await enableGeolocation();
      setLocationStatus('Position obtenue avec succès ! Redirection...');
      
      // Rediriger après un court délai pour montrer le message de succès
      setTimeout(() => {
        navigate('/prototype-categories');
      }, 1500);
    } catch (error) {
      // console.error('Erreur de localisation:', error);
      setLocationStatus('');
      setLocalError(error.message); // Stocker l'erreur localement
      setIsRetrying(true);
    }
  };

  const handleSkipLocation = async () => {
    setLocationStatus('Chargement du distributeur par défaut...');
    await skipGeolocation();
    navigate('/prototype-categories');
  };

  // Utiliser l'erreur du contexte ou l'erreur locale
  const currentError = localError || error;

  // Message d'aide contextuel selon l'erreur
  const getHelpMessage = () => {
    if (currentError?.includes('expiré')) {
      return (
        <div className="help-message">
        <SEOHead
          title="Choisir ma zone de livraison"
          description="Indiquez votre adresse pour voir les boissons disponibles près de chez vous à Douala."
          canonical="https://bccv.online/location"
        />
          <p><strong>Conseils pour améliorer la localisation :</strong></p>
          <ul>
            <li>• Sortez à l'extérieur ou près d'une fenêtre</li>
            <li>• Activez le Wi-Fi pour améliorer la précision</li>
            <li>• Désactivez le mode économie d'énergie</li>
            <li>• Patientez quelques secondes après l'activation du GPS</li>
          </ul>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="location-page">
      {/* Logo de l'application */}
      <img src={logoImage} alt="Boissons du Cameroun" className="location-logo" />
      
      {/* Icône de localisation animée */}
      <div className="location-icon-container">
        <svg viewBox="0 0 24 24" width="100%" height="100%" fill="currentColor">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
        </svg>
      </div>

      {/* Titre et instructions */}
      <h2 className="location-title">
        {isIOS ? "Activation requise pour iOS" : "Activez votre localisation"}
      </h2>
      
      <p className="location-description">
        Pour vous connecter au distributeur le plus proche et voir les produits disponibles dans votre zone, nous avons besoin de votre position.
      </p>

      {/* Instructions spécifiques pour iOS */}
      {isIOS && (
        <div className="ios-instructions">
          <p>
            <strong>Instructions iOS :</strong><br/>
            1. Appuyez sur "Activer ma position"<br/>
            2. Choisissez "Autoriser pendant l'utilisation"<br/>
            3. Patientez quelques secondes<br/>
            4. Si nécessaire, réessayez
          </p>
        </div>
      )}

      {/* Message d'aide contextuel */}
      {currentError && getHelpMessage()}

      {/* Bouton d'activation */}
      <button
        onClick={handleEnableLocation}
        disabled={loading}
        className="location-button"
      >
        {loading ? (
          <>
            <div className="loading-spinner"></div>
            <span>Recherche en cours...</span>
          </>
        ) : (
          <>
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" fill="currentColor"/>
            </svg>
            {isRetrying ? 'Réessayer' : 'Activer ma position'}
          </>
        )}
      </button>

      {/* Message de statut */}
      {locationStatus && (
        <div className={`location-status ${
          locationStatus.includes('succès') ? 'success' : 
          locationStatus.includes('Recherche') ? 'loading' : 
          'error'
        }`}>
          {locationStatus}
        </div>
      )}

      {/* Message d'erreur */}
      {currentError && !locationStatus && (
        <div className="location-status error">
          {currentError}
        </div>
      )}

      {/* Options alternatives */}
      <div className="alternative-options">
        {/* Bouton pour réessayer avec des paramètres différents */}
        {currentError && (
          <button
            onClick={() => {
              setLocalError(null); // Réinitialiser l'erreur locale
              handleEnableLocation();
            }}
            className="alternative-button"
          >
            Réessayer avec une précision réduite
          </button>
        )}

        {/* Lien pour passer la localisation */}
        <button
          onClick={handleSkipLocation}
          className="skip-button"
          disabled={loading}
        >
          Continuer sans localisation
        </button>
      </div>

      {/* Message pour iOS en cas de problème */}
      {isIOS && currentError && (
        <div className="ios-help-text">
          En cas de blocage persistant :<br/>
          1. Allez dans Réglages → Confidentialité → Service de localisation<br/>
          2. Activez "Service de localisation"<br/>
          3. Trouvez Safari et sélectionnez "Pendant l'utilisation de l'app"<br/>
          4. Revenez et réessayez
        </div>
      )}
    </div>
  );
};

export default LocationPage;